import { fleet as staticFleet } from '../data';
import { UsersIcon, ChevronLeftIcon, ChevronRightIcon, BoltIcon, FireIcon } from '@heroicons/react/24/outline';
import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import CarModal from './CarModal';
import { useAuth, useApi } from '../context/AuthContext';

// Map API vehicle data → shape expected by CarModal / fleet card
function apiVehicleToCard(v) {
  const isElectric = v.fuel?.toLowerCase().includes('electric') || v.teslaEnabled;
  const isHybrid   = v.fuel?.toLowerCase().includes('hybrid');
  return {
    // identity
    vin:    v.vin,
    name:   `${v.year} ${v.make} ${v.model}`,
    trim:   v.trim || '',
    seats:  v.seats || 5,
    fuel:   isElectric ? 'Electric' : isHybrid ? 'Hybrid' : (v.fuel || 'Gas'),
    fuelIcon: isElectric
      ? <BoltIcon className="w-6 h-6 mr-1" />
      : <FireIcon className="w-6 h-6 mr-1" />,
    desc:   v.description || '',
    price:  v.dailyRateCents ? Math.round(v.dailyRateCents / 100) : null,
    img:    v.imageUrl || '/images/model3.jpg',
    gallery: v.imageUrl ? [v.imageUrl] : [],
    status: v.status,   // 'available' | 'rented' | 'maintenance'
    specs:  v.specs || {},
    turoUrl: v.turoUrl || null,
  };
}

// Status badge shown on each card
function StatusBadge({ status }) {
  const map = {
    available:   { label: 'Available Today', cls: 'bg-green-100 text-green-700' },
    rented:      { label: 'Currently Rented', cls: 'bg-yellow-100 text-yellow-700' },
    maintenance: { label: 'Temporarily Unavailable', cls: 'bg-orange-100 text-orange-700' },
    unavailable: { label: 'Temporarily Unavailable', cls: 'bg-orange-100 text-orange-700' },
  };
  const { label, cls } = map[status] || { label: status, cls: 'bg-gray-100 text-gray-600' };
  return (
    <span className={`text-xs font-semibold px-2 py-0.5 rounded-full ${cls}`}>
      {label}
    </span>
  );
}

export default function Fleet() {
  const navigate = useNavigate();
  const { user, isLoggedIn } = useAuth();
  const api = useApi();

  // ── Data state ──────────────────────────────────────────────────────────────
  const [vehicles, setVehicles]   = useState([]);
  const [apiLoaded, setApiLoaded] = useState(false);
  const [apiError, setApiError]   = useState(false);

  // ── Verification state ──────────────────────────────────────────────────────
  const [isVerified, setIsVerified] = useState(false);

  // ── Carousel state ──────────────────────────────────────────────────────────
  const [selectedCar, setSelectedCar] = useState(null);
  const [currentIndex, setCurrentIndex] = useState(0);
  const [isHovering, setIsHovering]   = useState(false);
  const carsPerView      = 3;
  const maxIndex         = Math.max(0, vehicles.length - carsPerView);
  const autoRotateInterval = 5000;

  // ── Fetch live vehicles from API ────────────────────────────────────────────
  // Uses /vehicles/available (public, no auth required) so that:
  //   1. Unauthenticated visitors see live status without needing to log in.
  //   2. Maintenance vehicles are included as 'unavailable' (not hidden).
  //   3. Rented vehicles show as 'rented' so customers know to check back.
  // Falls back to static fleet data if the API is unreachable.
  useEffect(() => {
    const apiBase = process.env.REACT_APP_API_GATEWAY_URL;
    if (!apiBase) {
      setVehicles(staticFleet);
      setApiLoaded(true);
      return;
    }

    setApiLoaded(false);
    setApiError(false);

    fetch(`${apiBase}/vehicles`)
      .then((r) => {
        if (!r.ok) throw new Error(`HTTP ${r.status}`);
        return r.json();
      })
      .then((data) => {
        // /vehicles/ returns a plain array of vehicle objects (active + maintenance).
        // Each vehicle has a `status` field: 'available' | 'rented' | 'unavailable'.
        const items = Array.isArray(data) ? data : data.vehicles || data.items || [];
        if (items.length > 0) {
          setVehicles(items.map(apiVehicleToCard));
        } else {
          setVehicles(staticFleet);
        }
        setApiLoaded(true);
      })
      .catch((err) => {
        console.warn('Fleet API unavailable, using static data:', err.message);
        setVehicles(staticFleet);
        setApiError(true);
        setApiLoaded(true);
      });
  }, []);

  // ── Check verification status when logged in ────────────────────────────────
  useEffect(() => {
    if (!isLoggedIn) {
      setIsVerified(false);
      return;
    }
    api.get('/users/me/verify/status')
      .then(r => setIsVerified(r.data?.verificationStatus === 'verified'))
      .catch(() => setIsVerified(false));
  }, [isLoggedIn]);

  // ── Auto-rotate carousel ────────────────────────────────────────────────────
  useEffect(() => {
    if (selectedCar || isHovering || vehicles.length === 0) return;
    const interval = setInterval(() => {
      setCurrentIndex((prev) => (prev === maxIndex ? 0 : prev + 1));
    }, autoRotateInterval);
    return () => clearInterval(interval);
  }, [selectedCar, isHovering, maxIndex, vehicles.length]);

  const handlePrev = () => setCurrentIndex(Math.max(0, currentIndex - 1));
  const handleNext = () => setCurrentIndex(Math.min(maxIndex, currentIndex + 1));
  const visibleCars = vehicles.slice(currentIndex, currentIndex + carsPerView);

  // ── Loading skeleton ────────────────────────────────────────────────────────
  if (!apiLoaded) {
    return (
      <section id="vehicles" className="py-16 bg-gray-50 font-sans">
        <div className="max-w-7xl mx-auto px-4">
          <div className="text-center mb-12">
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900">Our Exclusive Fleet</h2>
            <p className="mt-4 text-lg text-gray-600 max-w-2xl mx-auto">
              Carefully selected vehicles that offer exceptional driving experiences.
            </p>
          </div>
          <div className="grid gap-8 md:grid-cols-3">
            {[1, 2, 3].map((i) => (
              <div key={i} className="bg-white rounded-lg shadow overflow-hidden animate-pulse">
                <div className="h-48 bg-gray-200" />
                <div className="p-6 space-y-3">
                  <div className="h-5 bg-gray-200 rounded w-3/4" />
                  <div className="h-4 bg-gray-200 rounded w-1/2" />
                  <div className="h-4 bg-gray-200 rounded w-full" />
                  <div className="h-4 bg-gray-200 rounded w-5/6" />
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>
    );
  }

  return (
    <section id="vehicles" className="py-16 bg-gray-50 font-sans">
      <div className="max-w-7xl mx-auto px-4">
        {/* Heading */}
        <div className="text-center mb-12" data-aos="fade-up">
          <h2 className="text-3xl md:text-4xl font-bold text-gray-900">
            Our Exclusive Fleet
          </h2>
          <p className="mt-4 text-lg text-gray-600 max-w-2xl mx-auto">
            Carefully selected vehicles that offer exceptional driving experiences.
          </p>
          {apiError && (
            <p className="mt-2 text-xs text-gray-400 italic">
              Showing cached fleet data — live availability requires sign-in.
            </p>
          )}
        </div>

        {/* Fleet Carousel */}
        <div
          className="relative"
          onMouseEnter={() => setIsHovering(true)}
          onMouseLeave={() => setIsHovering(false)}
        >
          {/* Left Arrow */}
          <button
            onClick={handlePrev}
            disabled={currentIndex === 0}
            className="absolute left-0 top-1/2 -translate-y-1/2 -translate-x-12 md:-translate-x-16 z-10 p-2 rounded-full bg-brandBlue text-white hover:bg-blue-800 transition disabled:opacity-50 disabled:cursor-not-allowed"
            aria-label="Previous vehicles"
          >
            <ChevronLeftIcon className="w-6 h-6" />
          </button>

          {/* Fleet Grid */}
          <div className="grid gap-8 md:grid-cols-3">
            {visibleCars.map((car, i) => (
              <div
                key={car.vin || (currentIndex + i)}
                className="bg-white rounded-lg shadow hover:shadow-xl transition-all duration-500 overflow-hidden flex flex-col transform hover:-translate-y-1"
                data-aos="fade-up"
                data-aos-delay={i * 150}
              >
                {/* Image */}
                <div className="overflow-hidden cursor-pointer relative" onClick={() => setSelectedCar(car)}>
                  <img
                    src={car.img}
                    alt={car.name}
                    className="w-full h-48 object-cover transform transition-transform duration-500 hover:scale-105"
                  />
                  {/* Live status badge (only shown for API vehicles) */}
                  {apiLoaded && !apiError && car.status && (
                    <div className="absolute top-2 right-2">
                      <StatusBadge status={car.status} />
                    </div>
                  )}
                </div>

                {/* Content */}
                <div className="p-6 flex flex-col flex-grow">
                  <h3 className="text-xl font-bold text-gray-900 mb-0">{car.name}</h3>
                  <p className="text-gray-600 text-sm mb-3 italic">{car.trim}</p>

                  {/* Specs */}
                  <div className="flex items-center text-gray-700 text-lg mb-4 space-x-4">
                    <span className="flex items-center">
                      <UsersIcon className="w-6 h-6 mr-1" />
                      {car.seats} seats
                    </span>
                    <span className="flex items-center">
                      {car.fuelIcon}
                      {car.fuel}
                    </span>
                  </div>

                  {/* Description */}
                  <p className="text-gray-600 flex-grow">{car.desc}</p>

                  {/* Price & CTA */}
                  <div className="mt-6 flex items-center justify-between">
                    <span className="text-lg font-bold text-brandBlue">
                      {car.price ? `$${car.price}/day` : ''}
                    </span>

                    {isVerified ? (
                      /* ── Verified user: private booking is primary ── */
                      <div className="flex flex-col items-end gap-1">
                        <button
                          onClick={() => navigate(`/book/${car.vin}`)}
                          disabled={car.status !== 'available'}
                          className="bg-brandBlue text-white px-4 py-2 rounded-md hover:bg-blue-800 transition duration-300 text-sm font-medium disabled:opacity-50 disabled:cursor-not-allowed"
                        >
                          {car.status === 'available' ? 'Book Now' : 'Unavailable'}
                        </button>
                        {car.turoUrl && car.status === 'available' && (
                          <a
                            href={car.turoUrl}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="text-xs text-gray-400 hover:text-blue-600 transition"
                          >
                            or book on Turo ↗
                          </a>
                        )}
                      </div>
                    ) : car.turoUrl ? (
                      /* ── Unverified + has Turo listing: send to Turo ── */
                      <a
                        href={car.turoUrl}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="bg-brandBlue text-white px-4 py-2 rounded-md hover:bg-blue-800 transition duration-300 text-sm font-medium"
                      >
                        View on Turo
                      </a>
                    ) : (
                      /* ── Unverified + no Turo listing: contact us ── */
                      <a
                        href="/#contact"
                        className="bg-brandBlue text-white px-4 py-2 rounded-md hover:bg-blue-800 transition duration-300 text-sm font-medium"
                      >
                        Request Booking
                      </a>
                    )}
                  </div>
                </div>
              </div>
            ))}
          </div>

          {/* Right Arrow */}
          <button
            onClick={handleNext}
            disabled={currentIndex === maxIndex}
            className="absolute right-0 top-1/2 -translate-y-1/2 translate-x-12 md:translate-x-16 z-10 p-2 rounded-full bg-brandBlue text-white hover:bg-blue-800 transition disabled:opacity-50 disabled:cursor-not-allowed"
            aria-label="Next vehicles"
          >
            <ChevronRightIcon className="w-6 h-6" />
          </button>
        </div>

        {/* Indicator dots */}
        <div className="mt-8 flex justify-center gap-2">
          {Array.from({ length: maxIndex + 1 }).map((_, i) => (
            <button
              key={i}
              onClick={() => setCurrentIndex(i)}
              className={`h-2 rounded-full transition ${
                i === currentIndex ? 'bg-brandBlue w-6' : 'bg-gray-300 w-2 hover:bg-gray-400'
              }`}
              aria-label={`Go to slide ${i + 1}`}
            />
          ))}
        </div>
      </div>

      {selectedCar && (
        <CarModal
          car={selectedCar}
          isOpen={!!selectedCar}
          onClose={() => setSelectedCar(null)}
        />
      )}
    </section>
  );
}
